import { lazy, Suspense, useEffect, useMemo, useState, useRef } from 'react';

// Lazily loaded: pdfjs is large, and most attachments are not PDFs.
const PdfPreview = lazy(() => import('./PdfPreview'));
import { X, ExternalLink, Download, Loader2, Pin, PinOff } from 'lucide-react';
import { apiDownloadUrl, apiGet } from '../lib/api';
import { EmailView, type EmailData } from './email/EmailView';
import { renderAsync } from 'docx-preview';
import { DOCX_MIME, emailPartMime, emailPartPath } from '../lib/emailParts';
import { parseCsv } from '../lib/csv';
import { CsvTable } from './CsvTable';

interface AttachmentPreviewModalProps {
  attachment: {
    id: string;
    filename: string;
    mime_type: string;
    pinned_from_date?: string | null;
    meeting_instance_id?: string | null;
    /**
     * Where to fetch the file's bytes, when it is not a stored attachment's
     * own download - one of an email's attachments (#321). Such a file is
     * never read as an email itself.
     */
    download_path?: string;
  };
  onClose: () => void;
  canPin?: boolean;
  canUnpin?: boolean;
  isPinned?: boolean;
  onPin?: () => void;
  onUnpin?: (mode: 'immediate' | 'future') => void;
}

/** Email attachments (#321): an Outlook .msg or an .eml, read by the server. */
const EMAIL_TYPES = new Set(['message/rfc822', 'application/vnd.ms-outlook']);
/** Shown as text (#321). Never HTML: text is put in the page as text, not markup. */
const TEXT_TYPES = new Set(['text/plain', 'text/csv', 'text/markdown', 'application/json']);
/** A text preview stops here; Download has the whole file. */
export const MAX_TEXT_PREVIEW = 1_000_000;

export function AttachmentPreviewModal({
  attachment,
  onClose,
  canPin,
  canUnpin,
  isPinned,
  onPin,
  onUnpin,
}: AttachmentPreviewModalProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnpinMenu, setShowUnpinMenu] = useState(false);
  const [emailData, setEmailData] = useState<EmailData | null>(null);
  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState<{ body: string; cut: boolean } | null>(null);
  /** Which of the email's own attachments is open on top of it (#321). */
  const [openPart, setOpenPart] = useState<number | null>(null);
  const [partError, setPartError] = useState<string | null>(null);

  const downloadPath = attachment.download_path ?? `/attachments/${attachment.id}/download`;

  const isImage = attachment.mime_type.startsWith('image/');
  const isPdf = attachment.mime_type === 'application/pdf';
  const isEml = !attachment.download_path && (EMAIL_TYPES.has(attachment.mime_type) || /\.(msg|eml)$/i.test(attachment.filename));
  const isDocx = attachment.mime_type === DOCX_MIME;
  const isText = TEXT_TYPES.has(attachment.mime_type);
  const isPreviewable = isImage || isPdf || isEml || isDocx || isText;
  const isCsv = attachment.mime_type === 'text/csv' || /\.csv$/i.test(attachment.filename);
  // A CSV shows as a table (#335) once it has at least two columns; otherwise as text.
  const csvRows = useMemo(() => {
    if (!isCsv || !text) return null;
    const rows = parseCsv(text.body);
    return rows.some((r) => r.length > 1) ? rows : null;
  }, [isCsv, text]);

  useEffect(() => {
    if (!isPreviewable) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    if (isEml) {
      apiGet<EmailData>(`/attachments/${attachment.id}/email`)
        .then((data) => { if (!cancelled) setEmailData(data); })
        .catch((err) => {
          if (cancelled) return;
          setError(String(err).includes('422')
            ? 'This email could not be read. Download it to open it in Outlook.'
            : 'Failed to load email preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else if (isText) {
      apiDownloadUrl(downloadPath)
        .then(async (url) => {
          const body = await (await fetch(url)).text();
          if (cancelled) return;
          setText({ body: body.slice(0, MAX_TEXT_PREVIEW), cut: body.length > MAX_TEXT_PREVIEW });
          setBlobUrl(url);
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else if (isDocx) {
      apiDownloadUrl(downloadPath)
        .then(async (url) => {
          const resp = await fetch(url);
          const blob = await resp.blob();
          if (!cancelled) {
            setDocxBlob(blob);
            setBlobUrl(url);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load document preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      apiDownloadUrl(downloadPath)
        .then((url) => {
          if (!cancelled) setBlobUrl(url);
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [attachment.id, downloadPath, isPreviewable, isEml, isDocx, isText]);

  // Render docx when blob and container are ready
  useEffect(() => {
    if (!docxBlob || !docxContainerRef.current) return;
    const container = docxContainerRef.current;
    container.innerHTML = '';
    renderAsync(docxBlob, container, undefined, {
      className: 'docx-preview',
      inWrapper: false,
      renderEndnotes: false,
      renderFootnotes: false,
      renderFooters: false,
      renderHeaders: false,
    }).catch(() => {
      setError('Failed to render document');
    });
  }, [docxBlob]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Close on Escape - unless one of the email's attachments is open on top, which Escape closes instead.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && openPart === null) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, openPart]);

  const saveAs = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /** Opens one of the email's own attachments in Dossier, or downloads it when Dossier cannot show it (#321). */
  const openEmailAttachment = async (index: number) => {
    const part = emailData?.attachments[index];
    if (!part) return;
    setPartError(null);
    if (emailPartMime(part.name, part.mime)) {
      setOpenPart(index);
      return;
    }
    try {
      const url = await apiDownloadUrl(emailPartPath(attachment.id, index));
      saveAs(url, part.name);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setPartError(`Could not download ${part.name}. Try again.`);
    }
  };
  const openPartData = openPart !== null ? emailData?.attachments[openPart] : undefined;

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
      onClose();
    }
  };

  const handleDownload = async () => {
    // Types that are not previewed (and emails) have no blob yet: fetch it now.
    let url = blobUrl;
    if (!url) {
      try {
        url = await apiDownloadUrl(downloadPath);
        setBlobUrl(url);
      } catch {
        setError('Download failed. Try again.');
        return;
      }
    }
    saveAs(url, attachment.filename);
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`bg-bg-secondary rounded-lg shadow-xl flex flex-col max-h-[90vh] ${
        isDocx || isEml || isText ? 'w-full max-w-[900px]' : 'max-w-[90vw] w-full sm:w-auto'
      } min-w-[320px]`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <span className="text-sm font-medium text-text-primary truncate flex-1">
            {attachment.filename}
          </span>
          {/* Pin/Unpin */}
          {isPinned && canUnpin && onUnpin && (
            <div className="relative">
              <button
                onClick={() => setShowUnpinMenu(!showUnpinMenu)}
                className="p-1 rounded-md text-accent hover:text-accent-hover hover:bg-bg-hover transition-colors"
                title="Unpin from series"
              >
                <PinOff size={14} />
              </button>
              {showUnpinMenu && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-bg-surface border border-border rounded-lg shadow-lg py-1 w-52">
                  <button
                    onClick={() => { onUnpin('immediate'); setShowUnpinMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                  >
                    Remove immediately
                    <span className="block text-[10px] text-text-muted mt-0.5">Gone from current + future meetings</span>
                  </button>
                  <button
                    onClick={() => { onUnpin('future'); setShowUnpinMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                  >
                    Remove from future only
                    <span className="block text-[10px] text-text-muted mt-0.5">Keeps through this meeting</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {canPin && !isPinned && onPin && (
            <button
              onClick={onPin}
              className="p-1 rounded-md text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
              title="Pin to series"
            >
              <Pin size={14} />
            </button>
          )}
          {blobUrl && isImage && (
            <button
              onClick={handleOpenInNewTab}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 min-h-[200px]">
          {loading ? (
            <Loader2 size={24} className="text-text-muted animate-spin" />
          ) : error ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-danger">{error}</p>
              {isEml && (
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
                >
                  <Download size={14} />
                  Download
                </button>
              )}
            </div>
          ) : isEml && emailData ? (
            <div className="w-full self-start">
              {partError && <p className="m-0 mb-2 text-sm text-danger">{partError}</p>}
              <EmailView email={emailData} onOpenAttachment={(i) => { void openEmailAttachment(i); }} />
            </div>
          ) : isText && text ? (
            <div className="w-full self-start space-y-2">
              {text.cut && (
                <p className="m-0 text-xs text-text-muted">
                  Showing the first {MAX_TEXT_PREVIEW.toLocaleString()} characters. Download it for the rest.
                </p>
              )}
              {csvRows ? <CsvTable rows={csvRows} /> : (
                <pre className="m-0 max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-bg-surface p-3 font-mono text-xs text-text-primary">
                  {text.body}
                </pre>
              )}
            </div>
          ) : isDocx && docxBlob ? (
            <div
              ref={docxContainerRef}
              className="w-full overflow-auto max-h-[70vh] bg-white rounded-lg p-6 text-black [&_table]:border-collapse [&_td]:border [&_td]:border-gray-300 [&_td]:p-1 [&_th]:border [&_th]:border-gray-300 [&_th]:p-1 [&_section:empty]:hidden [&_.docx-wrapper-footer]:hidden [&_footer]:hidden"
            />
          ) : isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={attachment.filename}
              className="max-w-full max-h-[70vh] object-contain rounded"
            />
          ) : isPdf && blobUrl && !pdfError ? (
            <Suspense
              fallback={<p className="py-8 text-center text-sm text-text-muted">Loading PDF viewer...</p>}
            >
              <PdfPreview src={blobUrl} filename={attachment.filename} onError={setPdfError} />
            </Suspense>
          ) : isPdf && pdfError ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-text-muted">{pdfError}</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
              >
                <Download size={14} />
                Download
              </button>
            </div>
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-text-muted">Preview not available for this file type</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
              >
                <Download size={14} />
                Download
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {isPreviewable && (blobUrl || emailData) && (
          <div className="flex items-center justify-end px-4 py-2.5 border-t border-border">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-bg-hover text-text-primary rounded-md hover:bg-bg-input transition-colors"
            >
              <Download size={12} />
              Download
            </button>
          </div>
        )}
      </div>
    </div>
    {openPart !== null && openPartData && (
      <AttachmentPreviewModal
        attachment={{
          id: `${attachment.id}:part-${openPart}`,
          filename: openPartData.name,
          mime_type: emailPartMime(openPartData.name, openPartData.mime),
          download_path: emailPartPath(attachment.id, openPart),
        }}
        onClose={() => setOpenPart(null)}
      />
    )}
    </>
  );
}
